import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';
import JobCard from './components/JobCard';
import EmployeeSpotlight from './components/EmployeeSpotlight';

const Careers = () => {
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior Full Stack Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Join our engineering team to build scalable solutions that connect couples with authentic wedding vendors. Work with React, Node.js, and modern cloud technologies.',
      requirements: ['React/JavaScript expertise', 'Node.js backend development', 'Database design', 'API development', 'Cultural sensitivity training'],
      postedDate: '2 days ago',
      applicants: '12'
    },
    {
      id: 2,
      title: 'Cultural Marketing Specialist',
      department: 'Marketing',
      location: 'Hybrid',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Drive marketing campaigns that resonate with South Asian communities. Create culturally authentic content and manage community engagement.',
      requirements: ['South Asian cultural knowledge', 'Content marketing', 'Social media strategy', 'Community management', 'Bilingual preferred'],
      postedDate: '1 week ago',
      applicants: '8'
    },
    {
      id: 3,
      title: 'Customer Success Manager',
      department: 'Customer Success',
      location: 'On-site',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Help couples and vendors succeed on our platform. Provide exceptional support and build lasting relationships within our community.',
      requirements: ['Customer service excellence', 'Wedding industry knowledge', 'Problem-solving skills', 'Cultural empathy', 'Communication skills'],
      postedDate: '3 days ago',
      applicants: '15'
    },
    {
      id: 4,
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Design beautiful, culturally-sensitive user experiences that honor South Asian wedding traditions while providing modern functionality.',
      requirements: ['UI/UX design expertise', 'Figma proficiency', 'Cultural design principles', 'User research', 'Mobile-first design'],
      postedDate: '5 days ago',
      applicants: '20'
    },
    {
      id: 5,
      title: 'Business Development Associate',
      department: 'Sales',
      location: 'Hybrid',
      type: 'Full-time',
      experience: '1-3 years',
      description: 'Expand our vendor network by building relationships with wedding service providers across major metropolitan areas.',
      requirements: ['Sales experience', 'Relationship building', 'Wedding industry connections', 'Negotiation skills', 'Cultural awareness'],
      postedDate: '1 week ago',
      applicants: '6'
    },
    {
      id: 6,
      title: 'Operations Coordinator',
      department: 'Operations',
      location: 'On-site',
      type: 'Full-time',
      experience: '1-2 years',
      description: 'Support day-to-day operations including vendor onboarding, quality assurance, and customer service coordination.',
      requirements: ['Operations experience', 'Process improvement', 'Attention to detail', 'Multi-tasking abilities', 'Team collaboration'],
      postedDate: '4 days ago',
      applicants: '9'
    }
  ];

  const employeeSpotlights = [
    {
      name: 'Anjali Reddy',
      role: 'Senior Software Engineer',
      department: 'Engineering',
      tenure: '3 years',
      location: 'Seattle, WA',
      achievement: 'Tech Innovation Award',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b9e3a56f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      quote: 'Working at ShaadiNest means building technology that celebrates my culture while helping thousands of couples create beautiful memories.',
      careerPath: ['Junior Developer (2022)', 'Software Engineer (2023)', 'Senior Engineer (2024)']
    },
    {
      name: 'Rahul Sharma',
      role: 'Marketing Director',
      department: 'Marketing',
      tenure: '2 years',
      location: 'New York, NY',
      achievement: 'Campaign Excellence',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      quote: 'Every campaign we create honors our traditions while reaching couples where they are. It\'s marketing with meaning and cultural authenticity.',
      careerPath: ['Marketing Specialist (2023)', 'Marketing Manager (2024)', 'Marketing Director (2025)']
    },
    {
      name: 'Kavya Patel',
      role: 'Customer Success Lead',
      department: 'Customer Success',
      tenure: '4 years',
      location: 'Austin, TX',
      achievement: 'Customer Champion',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      quote: 'Helping couples find their perfect vendors and seeing their dreams come true - that\'s what makes every day at ShaadiNest meaningful.',
      careerPath: ['Customer Support (2021)', 'Success Manager (2022)', 'Success Lead (2024)']
    }
  ];

  const companyBenefits = [
    {
      category: 'Health & Wellness',
      benefits: [
        'Comprehensive health insurance',
        'Mental health support',
        'Wellness stipend',
        'Flexible sick days'
      ],
      icon: 'Heart'
    },
    {
      category: 'Work-Life Balance',
      benefits: [
        'Flexible work arrangements',
        'Unlimited PTO policy',
        'Cultural holiday recognition',
        'Wedding planning support'
      ],
      icon: 'Home'
    },
    {
      category: 'Professional Growth',
      benefits: [
        'Learning & development budget',
        'Conference attendance',
        'Mentorship programs',
        'Career advancement paths'
      ],
      icon: 'TrendingUp'
    },
    {
      category: 'Compensation',
      benefits: [
        'Competitive salary',
        'Equity participation',
        'Performance bonuses',
        'Relocation assistance'
      ],
      icon: 'DollarSign'
    }
  ];

  const companyValues = [
    {
      title: 'Cultural Authenticity',
      description: 'We honor and celebrate South Asian traditions in everything we do',
      icon: 'Star'
    },
    {
      title: 'Innovation',
      description: 'We blend traditional values with cutting-edge technology',
      icon: 'Lightbulb'
    },
    {
      title: 'Community Building',
      description: 'We create connections that strengthen our cultural community',
      icon: 'Users'
    },
    {
      title: 'Excellence',
      description: 'We strive for the highest quality in products and service',
      icon: 'Award'
    }
  ];

  const departments = ['All', 'Engineering', 'Marketing', 'Customer Success', 'Design', 'Sales', 'Operations'];

  const filteredJobs = selectedDepartment === 'All' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  const handleApplyJob = (job) => {
    // In a real application, this would redirect to application form
    window.open(`mailto:careers@shaadinest.com?subject=Application for ${job.title}`, '_blank');
  };

  const handleContactHR = () => {
    navigate('/contact-us');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Careers - Join Our Mission | ShaadiNest</title>
        <meta 
          name="description" 
          content="Join ShaadiNest's mission-driven team. Discover career opportunities that celebrate South Asian wedding traditions through innovative technology and cultural authenticity." 
        />
        <meta name="keywords" content="careers, jobs, South Asian wedding, technology, cultural authenticity, remote work" />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
                  Build the Future of South Asian Weddings
                </h1>
                <p className="text-xl text-text-secondary mb-8">
                  Join our mission-driven team where technology meets tradition. Help us create meaningful connections between couples and vendors who understand the beauty and significance of South Asian wedding celebrations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="primary"
                    size="lg"
                    iconName="Search"
                    onClick={() => document.getElementById('job-openings')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    View Open Positions
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Users"
                    onClick={() => document.getElementById('company-culture')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Our Culture
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-w-16 aspect-h-12">
                  <img
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    alt="Diverse team collaboration"
                    className="w-full h-96 object-cover rounded-2xl shadow-strong"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-strong p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-2xl text-text-primary">50+</p>
                      <p className="text-sm text-text-secondary">Team Members</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Culture */}
        <section id="company-culture" className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Our Culture & Values
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                We're building more than a company - we're creating a movement that celebrates South Asian wedding traditions while embracing innovation and inclusivity.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {companyValues.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={value.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {value.title}
                  </h3>
                  <p className="text-text-secondary">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Employee Testimonials */}
            <div className="bg-surface-100 rounded-2xl p-8 lg:p-12">
              <div className="text-center mb-8">
                <h3 className="font-heading font-bold text-2xl text-text-primary mb-4">
                  What Our Team Says
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    quote: "Working at ShaadiNest means being part of something bigger - preserving cultural traditions while building innovative solutions.",
                    name: "Priya Singh",
                    role: "Product Manager"
                  },
                  {
                    quote: "The diversity and inclusion here isn't just a policy - it's lived every day in how we collaborate and celebrate each other.",
                    name: "Ahmed Hassan",
                    role: "DevOps Engineer"
                  },
                  {
                    quote: "I love that my technical skills directly impact couples' most important day. It's engineering with heart and purpose.",
                    name: "Neha Gupta",
                    role: "Frontend Developer"
                  }
                ].map((testimonial, index) => (
                  <div key={index} className="bg-white rounded-xl p-6">
                    <p className="text-text-secondary italic mb-4">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-medium text-text-primary">{testimonial.name}</p>
                      <p className="text-sm text-text-secondary">{testimonial.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Current Openings */}
        <section id="job-openings" className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Current Openings
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Join our growing team and help shape the future of South Asian wedding planning
              </p>
            </div>

            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedDepartment === dept
                      ? 'bg-primary text-white' :'bg-white text-text-secondary hover:bg-primary-50 hover:text-primary'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>

            {/* Job Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onApply={handleApplyJob}
                />
              ))}
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  No positions in {selectedDepartment}
                </h3>
                <p className="text-text-secondary mb-4">
                  Check back soon for new opportunities or explore other departments
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSelectedDepartment('All')}
                >
                  View All Positions
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Benefits & Perks
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                We believe in supporting our team members with comprehensive benefits that honor both professional and personal needs
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyBenefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl shadow-soft p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                    <Icon name={benefit.icon} size={20} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                    {benefit.category}
                  </h3>
                  <ul className="space-y-2">
                    {benefit.benefits.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-text-secondary text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diversity & Inclusion */}
        <section className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                  Diversity & Inclusion
                </h2>
                <p className="text-lg text-text-secondary mb-6">
                  Our strength comes from our diversity. We're committed to creating an inclusive environment that reflects the rich tapestry of communities we serve.
                </p>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">65%</div>
                    <div className="text-sm text-text-secondary">Diverse Leadership</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">15+</div>
                    <div className="text-sm text-text-secondary">Languages Spoken</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">40%</div>
                    <div className="text-sm text-text-secondary">Women in Tech</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">12+</div>
                    <div className="text-sm text-text-secondary">Cultural Backgrounds</div>
                  </div>
                </div>

                <p className="text-text-secondary">
                  We celebrate cultural holidays, provide bias training, and maintain employee resource groups that foster belonging and professional growth for all team members.
                </p>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Diverse team working together"
                  className="w-full h-96 object-cover rounded-2xl shadow-medium"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Employee Spotlight */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Employee Spotlight
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Meet some of our amazing team members and learn about their journey at ShaadiNest
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {employeeSpotlights.map((employee, index) => (
                <EmployeeSpotlight key={index} employee={employee} />
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-16 lg:py-20 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Application Process
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Our hiring process is designed to be transparent, respectful, and culturally inclusive
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                {
                  step: 1,
                  title: 'Application Review',
                  description: 'Submit your application and we\'ll review your background and cultural fit',
                  duration: '2-3 days',
                  icon: 'FileText'
                },
                {
                  step: 2,
                  title: 'Initial Screening',
                  description: 'Brief conversation about your experience and interest in our mission',
                  duration: '30 minutes',
                  icon: 'Phone'
                },
                {
                  step: 3,
                  title: 'Technical/Cultural Interview',
                  description: 'Role-specific interview focusing on skills and cultural understanding',
                  duration: '60 minutes',
                  icon: 'Users'
                },
                {
                  step: 4,
                  title: 'Team Meeting',
                  description: 'Meet potential teammates and leadership to ensure mutual fit',
                  duration: '45 minutes',
                  icon: 'Coffee'
                }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name={process.icon} size={24} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{process.step}</span>
                    </div>
                  </div>
                  
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    {process.title}
                  </h3>
                  
                  <p className="text-text-secondary mb-2">
                    {process.description}
                  </p>
                  
                  <span className="text-sm text-primary font-medium">
                    {process.duration}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <div className="bg-white rounded-xl shadow-soft p-6 max-w-2xl mx-auto">
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Timeline Expectations
                </h3>
                <p className="text-text-secondary mb-4">
                  Our entire process typically takes 1-2 weeks. We believe in moving quickly while being thorough and respectful of everyone's time.
                </p>
                <div className="flex items-center justify-center space-x-4 text-sm">
                  <span className="flex items-center space-x-1 text-text-secondary">
                    <Icon name="Clock" size={14} />
                    <span>1-2 weeks total</span>
                  </span>
                  <span className="flex items-center space-x-1 text-text-secondary">
                    <Icon name="MessageCircle" size={14} />
                    <span>Regular updates</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Growth */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary mb-6">
                Growing With Purpose
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Join us during an exciting phase of growth as we expand our impact across North America
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl p-6">
                <Icon name="TrendingUp" size={32} className="text-primary mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  Rapid Growth
                </h3>
                <p className="text-text-secondary mb-4">
                  300% year-over-year growth with expansion into 5 new metropolitan areas planned for 2025
                </p>
                <div className="text-2xl font-bold text-primary">300%</div>
                <div className="text-sm text-text-secondary">YoY Growth</div>
              </div>
              
              <div className="bg-gradient-to-br from-secondary-50 to-primary-50 rounded-xl p-6">
                <Icon name="Users" size={32} className="text-secondary mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  Team Expansion
                </h3>
                <p className="text-text-secondary mb-4">
                  Planning to double our team size with focus on engineering, marketing, and cultural operations
                </p>
                <div className="text-2xl font-bold text-secondary">2x</div>
                <div className="text-sm text-text-secondary">Team Growth</div>
              </div>
              
              <div className="bg-gradient-to-br from-success-50 to-primary-50 rounded-xl p-6">
                <Icon name="Award" size={32} className="text-success mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  Career Advancement
                </h3>
                <p className="text-text-secondary mb-4">
                  80% of leadership positions filled internally with clear advancement pathways for all roles
                </p>
                <div className="text-2xl font-bold text-success">80%</div>
                <div className="text-sm text-text-secondary">Internal Promotions</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Ready to Make an Impact?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join our mission to celebrate South Asian wedding traditions through innovative technology and authentic cultural connections.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Search"
                onClick={() => document.getElementById('job-openings')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Browse Open Positions
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Mail"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={handleContactHR}
              >
                Contact HR Team
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-2xl text-text-primary mb-4">
                Questions About Careers?
              </h2>
              <p className="text-text-secondary">
                Our recruitment team is here to help you find the perfect fit.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Mail" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Email Careers Team
                </h3>
                <p className="text-text-secondary">careers@shaadinest.com</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Phone" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Call Recruitment
                </h3>
                <p className="text-text-secondary">(555) 123-JOBS</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageCircle" size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Connect on LinkedIn
                </h3>
                <p className="text-text-secondary">@ShaadiNest-Careers</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Careers;